import React from 'react'
import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import isInteger from 'lodash/isInteger'
import { useCreateAndFulfillAdjustment } from '@ztechapps/adjustment.common'
import { Field, Form } from '@ztechapps/webui.form'
import { Modal, Stack } from 'react-bootstrap'
import { Alert, ShowIf } from '@ztechapps/webui.util'
import { InlineDisplayContent, TagsInput, TextAreaInput } from '@ztechapps/webui.input'
import { QuantityInputWithUom } from '@ztechapps/product.webui'
import { useFormikContext } from 'formik'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { useAdjustmentContext } from './Context'
import type { ProductQuantityProps } from '@ztechapps/webui.contract'
import { useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'
import { InventoryBySerialNumberInput } from '@ztechapps/inventorybyserialnumber.webui'
import { generateId } from '@ztechapps/common.utils'
import type { InventoryBySerialNumber, Product } from '@ztechapps/common.contract'

const AdjustmentFormDefaults = {
  quantity: 0,
  items: [],
  tags: null,
  remarks: 'Product quantity has been adjusted.',
  inventoryBySerialNumbers: [],
}

export type AdjustmentFormFields = typeof AdjustmentFormDefaults & {
  inventoryBySerialNumberValue?: {
    quantity: number
    inventoryBySerialNumbers: InventoryBySerialNumber[]
    product: Product
    _id: string
  }
}

export const AdjustmentForm = () => {
  const ProductQuantity = useInjectComponent<ProductQuantityProps>(WebuiIocKey.product.productQuantity)

  const { product, afterAdjustment, qtyToAdjust } = useAdjustmentContext()
  const billOfMaterials = product?.billOfMaterials
  const hasSerialNumber = product?.requiredSerialNumber

  const initialValues = React.useMemo<AdjustmentFormFields>(() => {
    const items = map(billOfMaterials, (m) => {
      return { ...m, included: m?.requiredToBuild, hasEnoughQty: m?.quantity < m?.product?.inventory?.quantityOnHand }
    })

    return {
      ...AdjustmentFormDefaults,
      quantity: qtyToAdjust ?? 1,
      items,
      inventoryBySerialNumbers: [],
      inventoryBySerialNumberValue: {
        quantity: qtyToAdjust ?? 1,
        inventoryBySerialNumbers: [],
        product: product,
        _id: generateId(), // Temporary ID for the adjustment item
      },
    }
  }, [billOfMaterials, qtyToAdjust, product])

  const validate = (values: AdjustmentFormFields) => {
    const errors: any = {}
    const quantity = values?.inventoryBySerialNumberValue?.quantity ?? 0
    const inventoryBySerialNumbers = values?.inventoryBySerialNumberValue?.inventoryBySerialNumbers ?? []

    if (quantity === 0) {
      errors.inventoryBySerialNumberValue = 'Must be a value more than or less than zero, (but not zero)'
    }
    if (isEmpty(values?.remarks)) {
      errors.remarks = 'Please provide some remarks'
    }

    if (product?.requiredSerialNumber && quantity < 0) {
      if (inventoryBySerialNumbers.length !== Math.abs(quantity)) {
        errors.inventoryBySerialNumberValue = 'Selected serial number count must match the quantity value.'
      }
      //need to check if the value is an integer
      if (!isInteger(quantity)) {
        errors.inventoryBySerialNumberValue = 'Quantity must be an integer if the product requires serial number.'
      }
    }

    return errors
  }

  const [onActionBase] = useCreateAndFulfillAdjustment(product?._id)

  // Transform inventoryBySerialNumberValue back to separate fields before submission
  const onAction = async (formValues: AdjustmentFormFields) => {
    // Filter inventoryBySerialNumbers to only include fields needed by the backend
    // For negative adjustments: needs _id to deactivate existing serial numbers
    // For positive adjustments: needs code to create new serial numbers
    const inventoryBySerialNumbers = (formValues.inventoryBySerialNumberValue?.inventoryBySerialNumbers ?? []).map(
      (sn) => ({
        _id: sn._id,
        code: sn.code,
      })
    ) as InventoryBySerialNumber[]

    const transformedValues = {
      ...formValues,
      quantity: formValues.inventoryBySerialNumberValue?.quantity ?? 0,
      inventoryBySerialNumbers,
    }
    return onActionBase(transformedValues)
  }

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={onAction}
      afterSubmit={afterAdjustment}
      afterReset={afterAdjustment}
    >
      {() => {
        return (
          <>
            <Modal.Body>
              <Stack gap={1}>
                <div>
                  <ShowIf condition={hasSerialNumber}>
                    <Field
                      name="inventoryBySerialNumberValue"
                      component={InventoryBySerialNumberInput}
                      label="Quantity"
                      help="The quantity that you adjust for this product."
                      onChange
                    />
                  </ShowIf>
                  <ShowIf condition={!hasSerialNumber}>
                    <Field
                      name="inventoryBySerialNumberValue.quantity"
                      component={QuantityInputWithUom}
                      symbol={product?.baseUom?.symbol}
                      label="Quantity"
                      help="The quantity that you adjust for this product."
                    />
                  </ShowIf>
                </div>
                <div>
                  <Field
                    name="remarks"
                    component={TextAreaInput}
                    label="Remarks"
                    help="Set a remarks about this adjustment, it will remind you in the future the reason for your changes."
                  />
                </div>
                <div>
                  <Field
                    name="tags"
                    component={TagsInput}
                    label="Tags"
                    help="Setting the tags to categorized this changes."
                    onChange
                  />
                </div>

                <div>
                  <InlineDisplayContent label="Inventory Count" meta={{ touched: true }}>
                    <ProductQuantity
                      hasSubComponents={product?.hasSubComponents}
                      quantityOnHand={product?.inventory?.quantityOnHand}
                      requiredComputedQuantityOnHand={product?.requiredComputedQuantityOnHand}
                      computedQuantityOnHand={product?.computedQuantityOnHand}
                      baseUomSymbol={product?.baseUom?.symbol}
                      decimalPrecision={product?.unitOfMeasurePrecision}
                    />
                  </InlineDisplayContent>
                </div>
              </Stack>
            </Modal.Body>

            <Modal.Footer className="gap-1">
              <Alert type="info" className="flex-fill p-0 m-0">
                <ul className="small my-0 py-2 ">
                  <li>Setting negative quantity will subtract from the current physical quantity count.</li>
                  <li>Setting positive quantity will add from the current physical quantity count.</li>
                  <li>If the product requires serial number, you need to set the serial numbers for adjustment.</li>
                </ul>
              </Alert>
              <div>
                <SaveCancelChangesButton saveText="Adjust" saveBusyText="Adjusting..." />
              </div>
            </Modal.Footer>
          </>
        )
      }}
    </Form>
  )
}

export const SaveCancelChangesButton = (props) => {
  const { isSubmitting } = useFormikContext()
  const { canEdit } = useAdjustmentContext()

  return <SaveCancelButtons disabled={!canEdit} isBusy={isSubmitting} saveType="submit" cancelType="reset" {...props} />
}
